import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import Switch from "@material-ui/core/Switch";
import TableCell from "@material-ui/core/TableCell";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ProductPrice from "@saleor/components/ProductPrice";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import {
  SortableTableBody,
  SortableTableRow
} from "@saleor/components/SortableTable";
import TableHead from "@saleor/components/TableHead";
import { ProductVariant_costPrice } from "@saleor/fragments/types/ProductVariant";
import { StaffTenantAssignment_tenant_mall } from "@saleor/staff/types/StaffTenantAssignment";
import React, { useEffect } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { renderCollection } from "../../../misc";
import { ListActions, ReorderAction } from "../../../types";
import {
  ProductDetails_product,
  ProductDetails_product_variants
} from "../../types/ProductDetails";
import ProductVariantSetDefault from "../ProductVariantSetDefault";

function getAvailabilityLabel(
  intl: IntlShape,
  warehouse: string,
  variant: ProductDetails_product_variants,
  numAvailable: number
): string {
  const variantStock = variant.stocks.find(s => s.warehouse.id === warehouse);

  if (!!warehouse) {
    if (!!variantStock) {
      if (variantStock.quantity > 0) {
        return intl.formatMessage(
          {
            defaultMessage:
              "{stockQuantity,plural,one{{stockQuantity} available} other{{stockQuantity} available}}",
            description: "product variant inventory"
          },
          {
            stockQuantity: variantStock.quantity
          }
        );
      } else {
        return intl.formatMessage({
          defaultMessage: "Unavailable",
          description: "product variant inventory"
        });
      }
    } else {
      return intl.formatMessage({
        defaultMessage: "Not stocked",
        description: "product variant inventory"
      });
    }
  } else {
    if (numAvailable > 0) {
      return intl.formatMessage(
        {
          defaultMessage: "{numAvailable} available",
          description: "product variant inventory"
        },
        {
          numAvailable
        }
      );
    } else {
      return intl.formatMessage({
        defaultMessage: "Unavailable",
        description: "product variant inventory"
      });
    }
  }
}

interface ProductVariantsProps extends ListActions {
  isStoreAdmin: boolean;
  disabled: boolean;
  mall: StaffTenantAssignment_tenant_mall;
  product: ProductDetails_product;
  variants: ProductDetails_product_variants[];
  fallbackPrice?: ProductVariant_costPrice;
  toggleVariant: (toggle: boolean) => void;
  onVariantReorder: ReorderAction;
  onRowClick: (id: string) => () => void;
  onSetDefaultVariant(variant: ProductDetails_product_variants);
  onVariantAdd?();
  onVariantsAdd?();
}

const numberOfColumns = 7;

export const ProductVariants: React.FC<ProductVariantsProps> = props => {
  const {
    disabled,
    mall,
    variants,
    product,
    fallbackPrice,
    onRowClick,
    onVariantAdd,
    onVariantsAdd,
    onVariantReorder,
    onSetDefaultVariant,
    isChecked,
    isStoreAdmin,
    selected,
    toggle,
    toggleAll,
    toggleVariant,
    toolbar
  } = props;

  const useStyles = makeStyles(
    theme => ({
      [theme.breakpoints.up("lg")]: {
        colActions: {
          width: 70
        },
        colInventory: {
          width: 200
        },
        colName: {},
        colPrice: {
          width: 135
        },
        colSku: {
          width: isStoreAdmin ? 200 : ""
        }
      },
      colGrab: {
        width: 60
      },
      colInventory: {
        textAlign: "right"
      },
      colName: {
        paddingLeft: 0
      },
      colPrice: {
        textAlign: "right"
      },
      colSku: {},
      colStatus: {},
      defaultVariant: {
        color: fade(theme.palette.text.secondary, 0.6),
        display: "block"
      },
      link: {
        cursor: "pointer"
      },
      select: {
        display: "inline-block"
      },
      textLeft: {
        textAlign: "left" as "left"
      },
      textRight: {
        textAlign: "right" as "right"
      },
      variantButton: {
        display: isStoreAdmin ? "none" : "block"
      },
      warehouseLabel: {
        display: "inline-block",
        marginRight: theme.spacing()
      },
      warehouseSelectContainer: {
        paddingTop: theme.spacing(2)
      }
    }),
    { name: "ProductVariants" }
  );

  const classes = useStyles(props);

  const intl = useIntl();
  const [warehouse] = React.useState<string>(null);
  const [variant, setVariant] = React.useState<boolean>(false);

  useEffect(() => {
    toggleVariant(variant);
  }, [variant]);

  useEffect(() => {
    if (product) {
      setVariant(!product.isSimpleProduct);
    }
  }, [product]);

  return (
    <Card>
      {variant && variants && variants.length > 0 ? null : (
        <CardTitle
          title={` `}
          toolbar={
            <FormControlLabel
              control={
                <Switch
                  id="doesProductHaveVariantsToggle"
                  name="isAddVariant"
                  checked={variant}
                  onChange={() => setVariant(prevVariant => !prevVariant)}
                  color="primary"
                  disabled={isStoreAdmin}
                />
              }
              label="Does product have variants?"
            />
          }
        />
      )}
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Variants",
          description: "section header"
        })}
        toolbar={
          variant ? (
            <Button
              id="createVariantBtn"
              onClick={onVariantAdd}
              variant="text"
              color="primary"
              data-test="button-add-variant"
              className={classes.variantButton}
            >
              <FormattedMessage
                defaultMessage="Create variant"
                description="button"
              />
            </Button>
          ) : (
            <div>
              {variant ? (
                <Button
                  id="createVariantsBtn"
                  onClick={onVariantsAdd}
                  variant="text"
                  color="primary"
                  data-test="button-add-variants"
                  className={classes.variantButton}
                >
                  <FormattedMessage
                    defaultMessage="Create variants"
                    description="button"
                  />
                </Button>
              ) : null}
            </div>
          )
        }
      />

      {isStoreAdmin &&
        (variants && variants.length > 0 ? (
          <CardContent className={classes.warehouseSelectContainer}>
            <Typography
              className={classes.warehouseLabel}
              id="availableWarehouseValue"
            >
              <FormattedMessage
                defaultMessage="Available inventory at: {warehouse}"
                description="variant stock status"
                values={{ warehouse: mall.name }}
              />
            </Typography>
          </CardContent>
        ) : (
          <CardContent>
            <Typography color={variant ? "textPrimary" : "textSecondary"}>
              <FormattedMessage defaultMessage="Use variants for products that come in a variety of versions for example different sizes or colors" />
            </Typography>
          </CardContent>
        ))}
      {variant && (
        <ResponsiveTable>
          {isStoreAdmin ? (
            <colgroup>
              <col className={classes.colName} />
              <col className={classes.colSku} />
              <col className={classes.colPrice} />
              <col className={classes.colInventory} />
            </colgroup>
          ) : (
            <colgroup>
              <col className={classes.colGrab} />
              <col />
              <col className={classes.colName} />
              <col className={classes.colSku} />
              <col className={classes.colPrice} />
              <col className={classes.colInventory} />
              <col className={classes.colActions} />
            </colgroup>
          )}

          <TableHead
            colSpan={numberOfColumns}
            selected={selected}
            disabled={disabled || isStoreAdmin}
            items={variants}
            toggleAll={toggleAll}
            toolbar={isStoreAdmin ? undefined : toolbar}
            hideCheckBox={isStoreAdmin}
            dragRows
          >
            <TableCell className={classes.colName}>
              <FormattedMessage
                defaultMessage="Variant"
                description="product variant name"
              />
            </TableCell>
            <TableCell className={classes.colSku}>
              <FormattedMessage defaultMessage="SKU" />
            </TableCell>
            <Hidden smDown={false}>
              <TableCell className={classes.colPrice}>
                <FormattedMessage
                  defaultMessage="Price"
                  description="product variant price"
                />
              </TableCell>
            </Hidden>
            <TableCell className={classes.colInventory}>
              <FormattedMessage
                defaultMessage="Inventory"
                description="product variant inventory status"
              />
            </TableCell>
            {isStoreAdmin ? null : (
              <TableCell className={classes.colActions}></TableCell>
            )}
          </TableHead>
          <SortableTableBody onSortEnd={onVariantReorder}>
            {renderCollection(variants, (variant, variantIndex) => {
              const isSelected = variant ? isChecked(variant.id) : false;
              const isDefault =
                variant && product?.defaultVariant?.id === variant?.id;
              const numAvailable = isStoreAdmin
                ? variant.stockOnMall.quantity
                : variant && variant.stocks
                ? variant.stocks.reduce(
                    (acc, s) => acc + s.quantity - s.quantityAllocated,
                    0
                  )
                : null;

              return (
                <SortableTableRow
                  selected={isSelected}
                  hover={!!variant}
                  onClick={onRowClick(variant && variant.id)}
                  key={variant ? variant.id : "skeleton"}
                  index={variantIndex || 0}
                  className={classes.link}
                  disableDrag={isStoreAdmin}
                >
                  <TableCell padding="checkbox">
                    {isStoreAdmin ? null : (
                      <Checkbox
                        id={`checkboxRow${variantIndex}`}
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(variant && variant.id)}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    className={classes.colName}
                    data-test="name"
                    id={`nameRow${variantIndex}`}
                  >
                    {variant ? variant.name || variant.sku : <Skeleton />}
                    {isDefault && (
                      <span className={classes.defaultVariant}>
                        {intl.formatMessage({
                          defaultMessage: "Default",
                          description: "default product variant indicator"
                        })}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className={classes.colSku}
                    data-test="sku"
                    id={`skuRow${variantIndex}`}
                  >
                    {variant ? variant.sku : <Skeleton />}
                  </TableCell>
                  <Hidden smDown={false}>
                    <TableCell
                      className={classes.colPrice}
                      data-test="price"
                      id={`priceRow${variantIndex}`}
                    >
                      {variant ? (
                        variant.price ? (
                          <ProductPrice money={variant.price} />
                        ) : fallbackPrice ? (
                          <ProductPrice money={fallbackPrice} />
                        ) : (
                          <Skeleton />
                        )
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </Hidden>
                  <TableCell
                    className={classes.colInventory}
                    data-test="inventory"
                    id={`inventoryRow${variantIndex}`}
                  >
                    {numAvailable === null ? (
                      <Skeleton />
                    ) : (
                      getAvailabilityLabel(
                        intl,
                        warehouse,
                        variant,
                        numAvailable
                      )
                    )}
                  </TableCell>
                  {isStoreAdmin ? null : (
                    <TableCell
                      className={classes.colActions}
                      data-test="actions"
                      id={`menuBtnRow${variantIndex}`}
                      onClick={e => e.stopPropagation()}
                    >
                      {variant?.id !== product?.defaultVariant?.id && (
                        <ProductVariantSetDefault
                          onSetDefaultVariant={() =>
                            onSetDefaultVariant(variant)
                          }
                        />
                      )}
                    </TableCell>
                  )}
                </SortableTableRow>
              );
            })}
          </SortableTableBody>
        </ResponsiveTable>
      )}
    </Card>
  );
};
ProductVariants.displayName = "ProductVariants";
export default ProductVariants;
